/**
 * Full-page two-pane document review interface.
 *
 * Left pane: PDF viewer with the source document
 * Right pane: Editable form with extracted data and provenance
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Save,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";
import { LowConfidenceWarning } from "@/components/data/ConfidenceBadge";
import { PDFViewer } from "@/components/data/PDFViewer";
import { ReviewForm } from "@/components/data/ReviewForm";
import {
  getDocumentFile,
  getDocumentData,
  getProvenance,
  updateDocumentData,
  updateDocumentPortfolio,
  approveDocument,
  rejectDocument,
  listDocuments,
  type DocumentDataResponse,
  type FieldProvenance,
  type PortfolioInvestment,
} from "@/lib/extraction-api";
import { useTenant } from "@/hooks/useTenant";
import { supabase } from "@/integrations/supabase/client";
import { FundLookupDialog } from "@/components/funds/FundLookupDialog";
import { findMatchingFunds, addFundAlias } from "@/lib/fund-matching";
import {
  createPositionFromDocument,
  linkDocumentToInstrument,
  type DocumentData,
} from "@/lib/document-to-position";


export default function DocumentReview() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgId } = useTenant();

  // Local state for edited data
  const [editedData, setEditedData] = useState<Record<string, unknown> | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [targetPage, setTargetPage] = useState<number | undefined>(undefined);
  const [highlightText, setHighlightText] = useState<string | undefined>(undefined);
  const [highlightKey, setHighlightKey] = useState(0);

  // State for portfolio changes
  const [portfolioChanges, setPortfolioChanges] = useState<PortfolioInvestment[] | null>(null);
  const [hasPortfolioChanges, setHasPortfolioChanges] = useState(false);

  // State for fund matching dialog
  const [fundLookupOpen, setFundLookupOpen] = useState(false);
  const [pendingApprovalData, setPendingApprovalData] = useState<{
    fundName: string;
    gpName: string;
    nextDocId: string | null;
  } | null>(null);

  // Fetch client ID for queue navigation
  const { data: clientData } = useQuery({
    queryKey: ["client-for-org", orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const { data, error } = await supabase
        .from("clients")
        .select("id")
        .eq("org_id", orgId)
        .limit(1)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!orgId,
  });

  const clientId = clientData?.id;

  // Fetch document queue for prev/next navigation
  const { data: queueData } = useQuery({
    queryKey: ["documents", clientId],
    queryFn: () => listDocuments({ clientId }),
    enabled: !!clientId,
  });

  // Calculate prev/next document IDs (pending = null or "pending_review")
  const pendingDocs = queueData?.documents
    ?.filter((d) => d.status === "completed" && (d.review_status === "pending_review" || d.review_status === null))
    ?.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) || [];

  const currentIndex = pendingDocs.findIndex((d) => d.id === documentId);
  const prevDocId = currentIndex > 0 ? pendingDocs[currentIndex - 1]?.id : null;
  const nextDocId = currentIndex < pendingDocs.length - 1 ? pendingDocs[currentIndex + 1]?.id : null;
  const queuePosition = currentIndex >= 0 ? currentIndex + 1 : null;
  const queueTotal = pendingDocs.length;

  // Fetch document data
  const {
    data: documentData,
    isLoading: dataLoading,
    error: dataError,
  } = useQuery({
    queryKey: ["document-data", documentId],
    queryFn: () => getDocumentData(documentId!),
    enabled: !!documentId,
  });

  // Fetch provenance data separately
  const { data: provenanceData } = useQuery({
    queryKey: ["document-provenance", documentId],
    queryFn: () => getProvenance(documentId!),
    enabled: !!documentId,
  });

  // Fetch PDF file
  const {
    data: pdfBlob,
    isLoading: pdfLoading,
    error: pdfError,
  } = useQuery({
    queryKey: ["document-file", documentId],
    queryFn: () => getDocumentFile(documentId!),
    enabled: !!documentId,
  });

  // Initialize edited data when document data loads
  useEffect(() => {
    if (documentData?.data) {
      setEditedData(documentData.data);
      setHasChanges(false);
    }
  }, [documentData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      updateDocumentData(documentId!, data),
    onSuccess: () => {
      toast.success("Changes saved");
      setHasChanges(false);
      queryClient.invalidateQueries({ queryKey: ["document-data", documentId] });
    },
    onError: (error) => {
      toast.error("Failed to save changes", {
        description: error.message,
      });
    },
  });

  // Approve mutation - navigation handled by caller
  const approveMutation = useMutation({
    mutationFn: () => {
      // Save any pending changes first, then approve
      const data = editedData || documentData?.data;
      if (hasChanges && data) {
        return updateDocumentData(documentId!, data).then(() =>
          approveDocument(documentId!, "User")
        );
      }
      return approveDocument(documentId!, "User");
    },
    onError: (error) => {
      toast.error("Failed to approve document", {
        description: error.message,
      });
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: () => rejectDocument(documentId!, "Rejected by user"),
    onSuccess: () => {
      toast.info("Document rejected");
      navigate("/data/ingestion");
    },
    onError: (error) => {
      toast.error("Failed to reject document", {
        description: error.message,
      });
    },
  });

  // Handle field changes
  const handleFieldChange = (field: string, value: unknown) => {
    setEditedData((prev) => {
      if (!prev) return { [field]: value };
      return { ...prev, [field]: value };
    });
    setHasChanges(true);
  };

  // Handle portfolio changes
  const handlePortfolioChange = (investments: PortfolioInvestment[], changed: boolean) => {
    setPortfolioChanges(investments);
    setHasPortfolioChanges(changed);
  };

  // Handle provenance click to navigate PDF and highlight text
  const handleProvenanceClick = (page: number, text: string) => {
    setTargetPage(page);
    setHighlightText(text);
    setHighlightKey((k) => k + 1);
  };

  // Handle save (document data + portfolio)
  const handleSave = async () => {
    try {
      // Save document field changes
      if (hasChanges && editedData) {
        await saveMutation.mutateAsync(editedData);
      }
      // Save portfolio changes
      if (hasPortfolioChanges && portfolioChanges && documentId) {
        await updateDocumentPortfolio(documentId, portfolioChanges, "User");
        setHasPortfolioChanges(false);
        queryClient.invalidateQueries({ queryKey: ["document-portfolio", documentId] });
        toast.success("Portfolio changes saved");
      }
    } catch (error) {
      toast.error("Failed to save changes", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  // Handle reject
  const handleReject = () => {
    rejectMutation.mutate();
  };

  // Navigate to previous document
  const handlePrev = () => {
    if (prevDocId) {
      navigate(`/data/review/${prevDocId}`);
    }
  };

  // Navigate to next document
  const handleNext = () => {
    if (nextDocId) {
      navigate(`/data/review/${nextDocId}`);
    }
  };

  // Approve and go to next (or back to queue)
  const handleApproveAndNext = async () => {
    // Capture nextDocId before mutation (it may change after approval)
    const targetNextDocId = nextDocId;
    const data = editedData || documentData?.data || {};
    const fundName = data.fund_name as string | undefined;
    const gpName = data.gp_fund_manager as string | undefined;

    try {
      // Save portfolio changes first if any
      if (hasPortfolioChanges && portfolioChanges && documentId) {
        await updateDocumentPortfolio(documentId, portfolioChanges, "User");
        queryClient.invalidateQueries({ queryKey: ["document-portfolio", documentId] });
      }

      // If fund name is extracted, try to match to master catalog
      if (fundName && orgId) {
        const matchResult = await findMatchingFunds(orgId, {
          name: fundName,
          issuer: gpName,
        });

        if (matchResult.shouldAutoLink && matchResult.bestMatch) {
          // High confidence match - auto-link and proceed
          await handleFundMatchAndApprove(
            matchResult.bestMatch.instrumentId,
            matchResult.bestMatch.instrumentName,
            targetNextDocId
          );
        } else {
          // No match or needs confirmation - show dialog
          setPendingApprovalData({
            fundName,
            gpName: gpName || "",
            nextDocId: targetNextDocId,
          });
          setFundLookupOpen(true);
        }
      } else {
        // No fund name - just approve without linking
        await proceedWithApproval(targetNextDocId);
      }
    } catch (error) {
      toast.error("Failed to process document", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  // Handle fund selection from dialog
  const handleFundSelected = async (instrumentId: string, instrumentName: string) => {
    setFundLookupOpen(false);
    if (pendingApprovalData) {
      await handleFundMatchAndApprove(
        instrumentId,
        instrumentName,
        pendingApprovalData.nextDocId
      );
      setPendingApprovalData(null);
    }
  };

  // Handle fund match and approval
  const handleFundMatchAndApprove = async (
    instrumentId: string,
    instrumentName: string,
    targetNextDocId: string | null
  ) => {
    if (!documentId || !orgId) return;

    try {
      // Link document to instrument
      await linkDocumentToInstrument(documentId, instrumentId);

      // Get family_id from current document context
      const data = editedData || documentData?.data || {};
      const familyId = (data as Record<string, unknown>).family_id as string | null || null;

      // Create position and metrics from document data
      await createPositionFromDocument({
        orgId,
        familyId,
        instrumentId,
        documentId,
        documentType: documentData?.document_type || "unknown",
        data: data as DocumentData,
      });

      // Add fund name as alias if it differs from instrument name
      const fundName = data.fund_name as string | undefined;
      if (fundName && fundName !== instrumentName) {
        await addFundAlias(orgId, instrumentId, fundName, "document_extraction");
      }

      toast.success("Document linked to fund", {
        description: `Matched to: ${instrumentName}`,
      });

      // Proceed with approval
      await proceedWithApproval(targetNextDocId);
    } catch (error) {
      toast.error("Failed to link document to fund", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  // Proceed with document approval
  const proceedWithApproval = async (targetNextDocId: string | null) => {
    approveMutation.mutate(undefined, {
      onSuccess: () => {
        // Invalidate document list cache
        queryClient.invalidateQueries({ queryKey: ["documents"] });

        if (targetNextDocId) {
          toast.success("Document approved", {
            description: "Moving to next document in queue...",
          });
          navigate(`/data/review/${targetNextDocId}`);
        } else {
          toast.success("All documents reviewed!", {
            description: "No more documents pending review.",
          });
          navigate("/data/ingestion");
        }
      },
    });
  };

  // Handle dialog cancel - don't approve the document
  const handleFundLookupCancel = () => {
    setFundLookupOpen(false);
    setPendingApprovalData(null);
    toast.info("Document approval cancelled", {
      description: "Please select or create a fund to approve this document.",
    });
  };

  // Get confidence level for warning
  const confidenceLevel = documentData?.classification_confidence as "high" | "medium" | "low" | null;
  const classificationReasoning = documentData?.classification?.reasoning || null;

  // Loading state
  if (dataLoading) {
    return (
      <div className="flex-1 space-y-4 p-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[calc(100vh-200px)]" />
      </div>
    );
  }

  // Error state
  if (dataError || pdfError) {
    const error = dataError || pdfError;
    return (
      <div className="flex-1 space-y-4 p-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Document Review</h1>
        </div>
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <div className="text-center text-destructive">
            <p className="font-semibold">Failed to load document</p>
            <p className="text-sm text-muted-foreground mt-1">
              {error instanceof Error ? error.message : "Unknown error"}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/data/ingestion")}
            >
              Return to Ingestion
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isProcessing =
    saveMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending;

  return (
    <div className="flex-1 flex flex-col h-screen">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/data/ingestion")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold">Review Extracted Data</h1>
              {queuePosition && queueTotal > 0 && (
                <span className="text-sm text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {queuePosition} of {queueTotal}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Verify and edit the extracted information before approving
            </p>
          </div>
        </div>

        {/* Navigation and action buttons */}
        <div className="flex items-center gap-2">
          {/* Prev/Next navigation */}
          <div className="flex items-center gap-1 mr-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handlePrev}
              disabled={!prevDocId || isProcessing}
              title="Previous document"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleNext}
              disabled={!nextDocId || isProcessing}
              title="Next document"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="w-px h-6 bg-border" />

          <Button
            variant="outline"
            onClick={handleSave}
            disabled={(!hasChanges && !hasPortfolioChanges) || isProcessing}
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </Button>
          <Button
            variant="outline"
            onClick={handleReject}
            disabled={isProcessing}
          >
            {rejectMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            Reject
          </Button>
          <Button onClick={handleApproveAndNext} disabled={isProcessing}>
            {approveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            Approve
          </Button>
        </div>
      </div>

      {/* Low confidence warning */}
      {(confidenceLevel === "low" || confidenceLevel === "medium") && (
        <div className="px-4 pt-2">
          <LowConfidenceWarning
            confidence={confidenceLevel}
            reasoning={classificationReasoning}
          />
        </div>
      )}

      {/* Two-pane layout */}
      <ResizablePanelGroup
        direction="horizontal"
        className="flex-1"
      >
        {/* PDF Viewer */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <PDFViewer
            file={pdfBlob || null}
            targetPage={targetPage}
            highlightText={highlightText}
            highlightKey={highlightKey}
            className="h-full"
          />
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Review Form */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <ReviewForm
            data={editedData || documentData?.data || {}}
            provenance={provenanceData?.provenance || null}
            classification={documentData?.classification || null}
            documentType={documentData?.document_type || null}
            documentId={documentId}
            onChange={handleFieldChange}
            onPortfolioChange={handlePortfolioChange}
            onProvenanceClick={handleProvenanceClick}
            className="h-full"
          />
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* Fund Lookup Dialog for matching documents to funds */}
      <FundLookupDialog
        open={fundLookupOpen}
        onOpenChange={(open) => {
          if (!open) handleFundLookupCancel();
        }}
        onFundSelected={handleFundSelected}
        initialName={pendingApprovalData?.fundName || ""}
        initialIssuer={pendingApprovalData?.gpName || ""}
      />
    </div>
  );
}
